const API_BASE_URL = import.meta.env.VITE_API_GESTION_CASOS || 'https://localhost:7162';

export interface PushSubscriptionKeys {
  p256dh: string;
  auth: string;
}

export interface PushSubscriptionData {
  endpoint: string;
  expirationTime?: number | null;
  keys: PushSubscriptionKeys;
}

export class NotificationService {
  private static instance: NotificationService;
  private registration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string | null = null;

  private constructor() {}

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  async initialize(): Promise<boolean> {
    if (!('serviceWorker' in navigator)) {
      console.warn('Service Workers no están soportados en este navegador');
      return false;
    }

    if (!('PushManager' in window)) {
      console.warn('Push Notifications no están soportadas en este navegador');
      return false;
    }

    try {
      // Registrar el service worker
      this.registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/'
      });

      console.log('Service Worker registrado correctamente:', this.registration.scope);

      // Esperar a que esté activo
      if (this.registration.installing) {
        await new Promise<void>((resolve) => {
          this.registration!.installing!.addEventListener('statechange', (e) => {
            if ((e.target as ServiceWorker).state === 'activated') {
              resolve();
            }
          });
        });
      }

      return true;
    } catch (error) {
      console.error('Error al registrar Service Worker:', error);
      return false;
    }
  }

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('Notifications API no está soportada');
      return 'denied';
    }

    const permission = await Notification.requestPermission();
    console.log('Permiso de notificaciones:', permission);
    return permission;
  }

  getPermission(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  async getVapidPublicKey(): Promise<string> {
    if (this.vapidPublicKey) {
      return this.vapidPublicKey;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/Notifications/vapid-public-key`);
      if (!response.ok) {
        throw new Error('Error al obtener la clave pública VAPID');
      }

      const data = await response.json();
      // El backend retorna { publicKey: "..." } o { PublicKey: "..." }
      this.vapidPublicKey = data.publicKey || data.PublicKey;

      if (!this.vapidPublicKey) {
        throw new Error('Clave pública VAPID no encontrada en la respuesta');
      }

      console.log('[NotificationService] VAPID public key obtenida:', this.vapidPublicKey);
      return this.vapidPublicKey;
    } catch (error) {
      console.error('Error obteniendo clave VAPID:', error);
      throw error;
    }
  }

  urlBase64ToUint8Array(base64String: string): Uint8Array { 
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async subscribeToPushNotifications(token: string): Promise<PushSubscriptionData | null> {
    try {
      if (!this.registration) {
        throw new Error('Service Worker no está registrado');
      }

      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        console.warn('Permiso de notificaciones denegado');
        return null;
      }

      const vapidPublicKey = await this.getVapidPublicKey();
      const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

      // Verificar si ya existe una suscripción
      let subscription = await this.registration.pushManager.getSubscription();

      if (!subscription) {
        // Crear nueva suscripción
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
      }

      const subscriptionData: PushSubscriptionData = {
        endpoint: subscription.endpoint,
        expirationTime: subscription.expirationTime,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')!))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')!)))
        }
      };

      // Enviar la suscripción al servidor
      const response = await fetch(`${API_BASE_URL}/api/Notifications/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscriptionData)
      });

      if (!response.ok) {
        throw new Error('Error al guardar la suscripción en el servidor');
      }

      console.log('Suscripción a notificaciones push exitosa');
      return subscriptionData;
    } catch (error) {
      console.error('Error al suscribirse a notificaciones push:', error);
      return null;
    }
  }

  async unsubscribeFromPushNotifications(token: string): Promise<boolean> {
    try {
      if (!this.registration) {
        throw new Error('Service Worker no está registrado');
      }

      const subscription = await this.registration.pushManager.getSubscription();
      if (!subscription) {
        console.warn('No hay suscripción activa');
        return false;
      }

      // Desuscribirse del navegador
      const unsubscribed = await subscription.unsubscribe();

      if (unsubscribed) {
        // Notificar al servidor
        await fetch(`${API_BASE_URL}/api/Notifications/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ endpoint: subscription.endpoint })
        });

        console.log('Desuscripción de notificaciones push exitosa');
      }

      return unsubscribed;
    } catch (error) {
      console.error('Error al desuscribirse de notificaciones push:', error);
      return false;
    }
  }

  async getSubscription(): Promise<PushSubscription | null> {
    if (!this.registration) {
      return null;
    }
    return await this.registration.pushManager.getSubscription();
  }

  async showNotification(title: string, options?: NotificationOptions): Promise<void> {
    if (!this.registration) {
      throw new Error('Service Worker no está registrado');
    }

    await this.registration.showNotification(title, {
      icon: '/vite.svg',
      badge: '/vite.svg',
      ...options
    });
  }
}

export const notificationService = NotificationService.getInstance();
